import { translate as $t } from '@wealthbar/i18n';
import {
  fetchClients, fetchInvitations, inviteClient, updateClient, updateFee, deleteClientInvitation, unlinkClientFromAdvisor,
} from '@/lib/api';

export default {
  state: {
    clients: [],
    loading: false,
  },

  getters: {
    isLoadingClients(state) {
      return state.loading;
    },
    allClients(state) {
      return state.clients;
    },
    hasClients(state) {
      return state.clients.length > 0;
    },
    getClient(state) {
      return id => state.clients.find(c => c.id.toString() === id.toString());
    },
    searchClients(state) {
      function alphabeticalSort(clients) {
        return clients.sort(
          (client1, client2) => {
            if (client1.firstName < client2.firstName) {
              return -1;
            }
            if (client1.firstName > client2.firstName) {
              return 1;
            }
            if (client1.lastName < client2.lastName) {
              return -1;
            }
            return 1;
          },
        );
      }
      return (query) => {
        const startingWithRegex = new RegExp(`^${query}`, 'i');
        const containingRegex = new RegExp(`^.+${query}`, 'i');

        let resultsStartingWith = [];
        let resultsContaining = [];

        state.clients.forEach((client) => {
          const firstName = client.firstName || client.email;
          const lastName = client.lastName || client.email;

          if (firstName.match(startingWithRegex) || lastName.match(startingWithRegex)) {
            resultsStartingWith.push(client);
          } else if (firstName.match(containingRegex) || lastName.match(containingRegex)) {
            resultsContaining.push(client);
          }
        });

        resultsStartingWith = alphabeticalSort(resultsStartingWith);
        resultsContaining = alphabeticalSort(resultsContaining);

        return resultsStartingWith.concat(resultsContaining);
      };
    },

    getTotalAssets(state) {
      let assetTotal = 0;
      state.clients.forEach((client) => {
        assetTotal += client.assets;
      });
      return assetTotal;
    },
  },

  mutations: {
    setLoaderClients(state, loading) {
      state.loading = loading;
    },
    updateClient(state, updatedClient) {
      const client = state.clients.find(stateClient => stateClient.id === updatedClient.id);
      Object.assign(client, updatedClient);
    },
    updateClients(state, clients) {
      state.clients = clients;
    },
    deleteClient(state, deletedClientId) {
      state.clients = state.clients.filter(client => client.id !== deletedClientId);
    },
    deleteUnlinkedClient(state, unlinkedClientId) {
      state.clients = state.clients.filter(client => client.id !== unlinkedClientId);
    },
    addOrReplaceClient(state, clientToAdd) {
      const existingClientIndex = state.clients.findIndex(client => client.email === clientToAdd.email);
      if (existingClientIndex > -1) {
        state.clients.splice(existingClientIndex, 1);
      }
      state.clients.push(clientToAdd);
    },
  },

  actions: {
    async updateClient({ commit }, client) {
      const updatedClient = await updateClient.execute(client);
      commit('updateClient', updatedClient);
    },
    async updateFee({ commit, dispatch }, feeUpdate) {
      const feeResult = await updateFee.execute(feeUpdate);
      commit('updateClient', feeResult);
      dispatch('showNotification', { icon: '/images/money.png', message: $t('Fee has been changed.') });
    },
    async deleteClient({ commit, dispatch }, deletedClientId) {
      const deletedClientResponse = await deleteClientInvitation.execute(deletedClientId);
      commit('deleteClient', deletedClientResponse);
      dispatch('showNotification', { message: $t('Client removed.') });
    },
    async unlinkUnfundedClient({ commit, dispatch }, unlinkedClientId) {
      const unlinkedClientResponse = await unlinkClientFromAdvisor.execute(unlinkedClientId);
      commit('deleteUnlinkedClient', unlinkedClientResponse);
      dispatch('showNotification', { message: $t('Client removed.') });
    },
    async fetchClients({ commit }) {
      commit('setLoaderClients', true);
      const clients = await Promise.all([fetchClients.execute(), fetchInvitations.execute()]);
      commit('updateClients', [].concat(...clients));
      commit('setLoaderClients', false);
    },
    async inviteClient({ commit, dispatch }, invitation) {
      const data = await inviteClient.execute(invitation);
      const message = [];
      if (data.hasBeenAccepted) {
        message.push($t('This '));
        message.push({
          href: `/app/clients/${data.clientId}`,
          label: 'client',
        });
        message.push($t(' has already joined.'));
      } else {
        commit('addOrReplaceClient', data.invitation);
        if (data.isUpdate) {
          message.push($t('Invite has been resent.'));
        } else {
          message.push($t('Invite sent. You will be notified when the client signs up.'));
        }
      }
      dispatch('showNotification', { icon: '/images/tada.png', message });
    },
  },
};
